import React, { useState, useEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';

import {
  Checkbox,
  Chip,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '../../base';

import FileInput from '../../FileInput/FileInput';

import {
  titleHeight,
  getOptionsClass,
} from '../constants';

const styles = theme => ({
  questionContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: `calc(100vh - ${titleHeight}px)`,
  },
  questionHeader: {
    alignSelf: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: theme.spacing(),

    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(3),
    },
  },
  questionTitle: {
    textAlign: 'center',
    width: '100vw',
    fontSize: 20,
    marginBottom: theme.spacing(),

    [theme.breakpoints.up('sm')]: {
      fontSize: 24,
    },

    [theme.breakpoints.up('md')]: {
      fontSize: 30,
    },
  },
  multipleCaption: {

    [theme.breakpoints.up('md')]: {
      fontSize: 18,
    }
  },
  question: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    overflowY: 'auto',
  },
  moreQuestions: {
    justifyContent: 'flex-start !important'
  },
  selectContainer: {
    height: '80%',
    justifyContent: 'flex-start',
    alignContent: 'flex-start',

    '@media only screen and (min-width: 768px)': {
      marginTop: -60,
      alignContent: 'center',
      justifyContent: 'center',
    },

    [theme.breakpoints.up('lg')]: {
      height: '100%',
    },

    '&.col2': {
      alignContent: 'flex-start',
      marginTop: 0,

      [theme.breakpoints.up('md')]: {
        marginTop: 60,
      },
    },
  },
  optionContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),

    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(3),
    },
  },
  selectOption: {
    fontSize: 16,
    width: '100%',
    alignSelf: 'center',
    height: 40,
    marginBottom: theme.spacing(),

    '&:hover': {
      background: 'inherit',
    },

    '& input': {
      height: 40,
      zIndex: 40,
    },

    '& > span > div': {
      padding: theme.spacing(1, 0),
      fontSize: 16,
      height: 40,

      [theme.breakpoints.up('sm')]: {
        fontSize: 24,
        width: '100%',

        '@media (orientation: landscape)': {
          fontSize: 16,

          [theme.breakpoints.up('md')]: {
            fontSize: 24,
            height: 70,
          },
        },
      },
    },

    '&.Mui-checked:hover': {
      background: 'inherit',
    },

    '&.col2': {
      width: '100%',
    },

    [theme.breakpoints.up('sm')]: {
      fontSize: 24,
      width: '70%',

      '& input': {
        height: 70,
        marginTop: -27,
      },

      '& > span > div': {
        padding: theme.spacing(3),
      },
    },

    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(3),
    },
  },
  selectNotSelected: {
    filter: 'grayscale(100%)',
  },
  fileUploadContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  uploadButtonContainer: {
    flex: 1,
    width: '100%',
    background: 'white',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'center',

    '& > div:first-child': {
      flex: 1,
    }
  },
})

function ActionModeQuestion({
  classes: c,
  t,
  question,
  saveAnswer,
  uploadEndpoint,
  rotateImageEndpoint,
  additionalFilesField,
  answers, setSAPCauseCodes,
  jobId
}) {
  const [answer, setAnswer] = useState(answers[question.key]);
  useEffect(() => saveAnswer(question.key, answer, question), [answer]);

  useEffect(() => setAnswer(answers[question.key]), [question.key]);

  const handleSelect = e => {
    const value = e.target.value;
    
    question.key.includes('SAPCauseCodeGroup') && setSAPCauseCodes(e.target.value)

    if (answer !== value) setAnswer(value)
  }

  // Deselect answer
  const handleClick = e => {
    if (e.target.value === answer) {
      setAnswer(null);
    }
  }

  const handleMultiSelect = e => {
    const value = e.target.value;

    if (answer) {
      answer.includes(value)
      ? setAnswer(answer.filter(el => el !== value))
      : setAnswer([...answer, value])
    } else {
      setAnswer([value])
    }
  }

  const renderSelectQuestion = ({ options, label }) => {
    const optionsClass = options && options.length ? getOptionsClass(options.length) : '';

    return (
      <RadioGroup
        className={classNames(c.selectContainer, optionsClass, options && options.length && options.length > 12 ? c.moreQuestions : '')}
        aria-label={label}
        name={label}
        value={answer ? (!Array.isArray(answer) ? answer : '') : ''}
        onChange={handleSelect}
        onClick={handleClick}
      >
        <Grid container>
          {options && options.map(({value, label}) =>
            <Grid className={c.optionContainer} key={value} item xs={options.length > 5 ? 6 : 12}>
              <Radio
                disableRipple
                classes={{ root: classNames(c.selectOption, optionsClass) }}
                value={value}
                icon={<Chip variant='outlined' color='secondary' label={label} className={classNames(c.selectOption, answer ? c.selectNotSelected : '')} />}
                checkedIcon={<Chip className={c.selectOption} color='secondary' label={label}/>}
              />
            </Grid>
          )}
        </Grid>
      </RadioGroup>
    );
  }

  const renderMultiselectQuestion = ({ options }) => {
    const optionsClass = options && options.length ? getOptionsClass(options.length) : '';

    return (
      <Grid container className={classNames(c.selectContainer, optionsClass, 'multiselect')}>
        {options && options.map(({value, label}) =>
          <Grid className={c.optionContainer} key={value} item xs={options.length > 5 ? 6 : 12}>
            <Checkbox
              classes={{ root: classNames(c.selectOption, optionsClass) }}
              value={value}
              checked={answer ? answer.includes(value) : false}
              disableRipple
              onChange={handleMultiSelect}
              icon={<Chip className={options.length > 5 ? c.selectOption : c.selectOptionSmall} color='secondary' variant='outlined' key={value} label={label} />}
              checkedIcon={<Chip className={c.selectOption} color='secondary' key={value} label={label} />}
            />
          </Grid>
        )}
      </Grid>
    );
  }

  const renderUploadQuestion = q => {
    //Add location for files uploaded from computer
    q.files.map(file => file.location = q.key);

    const { section } = q;
    let files = q.files;

    const questionAnswerSession = JSON.parse(sessionStorage.getItem('questionAnswerSession'));
    if (
      questionAnswerSession &&
      questionAnswerSession[jobId] &&
      questionAnswerSession[jobId][section] &&
      questionAnswerSession[jobId][section].files
    ) {
      files = files.concat(questionAnswerSession[jobId][section].files);
    }

    return(
      <FileInput
        jobId={jobId}
        key={question.key}
        field={q}
        isDisabled={false}
        renderIsRequired={() => {}}
        files={files || []}
        uuids={q.answer || []}
        uploadEndpoint={uploadEndpoint}
        rotateImageEndpoint={rotateImageEndpoint}
        handleFieldChange={saveAnswer}
        isActionMode
        hasSpecial
        takePictureLabel={t.takeStandardPicture}
        takeSpecialPictureLabel={t.takeNonStandardPicture}
        additionalUuids={additionalFilesField && additionalFilesField.answer}
        answers={answers}
      />
    )
  }
  const renderProperUI = q => {
    switch (q.type) {
      case 'select':
        return renderSelectQuestion(q);
      case 'multiselect':
        return renderMultiselectQuestion(q);
      case 'file':
        return (
          <div className={c.fileUploadContainer}>
            <div className={c.uploadButtonContainer}>
              {renderUploadQuestion(q)}
            </div>
          </div>
        );
      default:
        return
    }
  };

  return (
    <div className={c.questionContainer}>
      <header className={c.questionHeader}>
        <Typography variant='h6' className={c.questionTitle}>
          {question.parentLabel ? `${question.parentLabel} - ` : ''}{question.label}
        </Typography>
        {
          question.type === 'multiselect' &&
          <Typography variant='caption' className={c.multipleCaption}>(multiple choices)</Typography>
        }
      </header>
      <div className={c.question}>{renderProperUI(question)}</div>
    </div>
  );
}

export default withStyles(styles)(ActionModeQuestion);



// WEBPACK FOOTER //
// ./src/components/ActionMode/ActionModeQuestion/ActionModeQuestion.js